<script setup>
import {Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {Icon} from "@iconify/vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import Games from "@/views/orders/Filters/Games.vue";
import Stores from "@/views/orders/Filters/Stores.vue";
import States from "@/views/orders/Filters/States.vue";
import ShopStates from "@/views/orders/Filters/ShopStates.vue";
import PaymentMethods from "@/views/orders/Filters/PaymentMethods.vue";
import Dates from "@/views/orders/Filters/Dates.vue";
import Button from "@/components/Button/index.vue";
import {ref, watch} from "vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import PersianDate from '@alireza-ab/persian-date';

const ordersStore = useMainOrdersStore();
const emit = defineEmits(['closeModal', 'applyFilter']);
const props = defineProps({
  filters: {
    type: Array,
    default: []
  },
  dateFields: {
    type: Array,
    default: ['createdFrom', 'createdTo', 'updatedFrom', 'updatedTo']
  },
  service: {
    type: String
  }
})
const counter = ref(0)

const myFilters = ref({
  stores: ordersStore.filters.stores.length > 0 ? ordersStore.filters.stores : [],
  games: ordersStore.filters.games.length > 0 ? ordersStore.filters.games : [],
  states: ordersStore.filters.states.length > 0 ? ordersStore.filters.states : [],
  shopStates: ordersStore.shopStateFilter.length > 0 ? ordersStore.shopStateFilter : [],
  paymentMethods: ordersStore.paymentMethodsFilter.length > 0 ? ordersStore.paymentMethodsFilter : [],
  'createdFrom': ordersStore.filters.createdFrom ? convertTimestamp(ordersStore.filters.createdFrom) : null,
  'createdTo': ordersStore.filters.createdTo ? convertTimestamp(ordersStore.filters.createdTo) : null,
  'updatedFrom': ordersStore.filters.updatedFrom ? convertTimestamp(ordersStore.filters.updatedFrom) : null,
  'updatedTo': ordersStore.filters.updatedTo ? convertTimestamp(ordersStore.filters.updatedTo) : null,
})

function convertTimestamp(timestamp) {
  let p = new PersianDate(Number(timestamp));
  return p.toString('YYYY-MM-DD');
}

function closeModal() {
  emit('closeModal');
  ordersStore.close_filter_modal();
}

async function applyFilters() {
  closeModal()
  ordersStore.filters.stores = myFilters.value.stores
  ordersStore.filters.games = myFilters.value.games
  ordersStore.filters.states = myFilters.value.states
  ordersStore.filters.createdFrom = myFilters.value.createdFrom
  ordersStore.filters.createdTo = myFilters.value.createdTo
  ordersStore.filters.updatedFrom = myFilters.value.updatedFrom
  ordersStore.filters.updatedTo = myFilters.value.updatedTo
  ordersStore.shopStateFilter = myFilters.value.shopStates
  ordersStore.paymentMethodsFilter = myFilters.value.paymentMethods
  emit('applyFilter');
}

function updateStores(stores) {
  myFilters.value.stores = stores
}

function updatePaymentMethods(paymentMethods) {
  myFilters.value.paymentMethods = paymentMethods
}

function updateGames(games) {
  myFilters.value.games = games
}

function updateStates(states) {
  myFilters.value.states = states
}

function updateShopStates(states) {
  myFilters.value.shopStates = states
}

function updateCreatedFrom(createdFrom) {
  myFilters.value.createdFrom = createdFrom;
}

function updateCreatedTo(createdTo) {
  myFilters.value.createdTo = createdTo;
}

function updateUpdatedFrom(updatedFrom) {
  myFilters.value.updatedFrom = updatedFrom;
}

function updateUpdatedTo(updatedTo) {
  myFilters.value.updatedTo = updatedTo;
}

function resetFilters() {
  counter.value++
  myFilters.value = {
    stores: [],
    games: [],
    states: [],
    shopStates: [],
    'createdFrom': null,
    'createdTo': null,
    'updatedFrom': null,
    'updatedTo': null,
  }
}
</script>

<template>
  <TransitionRoot appear show as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[9999]">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-[black]/60"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex h-full overflow-hidden items-center justify-center px-4 py-8">
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="w-full flex flex-col overflow-hidden rounded-md max-w-3xl text-left align-middle shadow-xl transition-all bg-white dark:bg-black-800 text-black-600 dark:text-white">
              <div class="flex flex-row justify-between px-4 border-b border-slate-700">
                <div class="text-base py-3">
                  Filters
                </div>
                <div class="flex flex-row items-center gap-4">
                  <button @click="resetFilters"
                          class="btn shadow-none flex flex-row items-center gap-2 p-2 text-xs h-8 font-normal hover:bg-slate-700">
                    Reset Filters
                  </button>
                  <div class="h-full w-[1px] bg-slate-700"></div>
                  <button
                      type="button"
                      class="text-white-dark outline-none hover:text-red-500"
                      @click="closeModal"
                  >
                    <Icon icon="pajamas:close-xs"/>
                  </button>
                </div>
              </div>
              <div class="p-5 flex-1 overflow-auto h-full flex flex-col gap-6">
                <div class="grid grid-cols-4 gap-2">
                  <div v-if="filters.includes('stores')" class="flex flex-col gap-1 text-sm">
                    <p>Stores</p>
                    <div @click="ordersStore.show_stores_modal()"
                         class="cursor-pointer flex flex-row relative bg-slate-900 items-center justify-between rounded-lg">
                      <div class="flex-1 flex flex-row justify-between items-center p-2">
                        <div class="">
                          {{ myFilters.stores.length }} Stores
                        </div>
                        <span class="flex-none flex flex-row gap-2 h-full items-center">
                          <Icon icon="cuida:caret-down-outline"/>
                        </span>
                      </div>
                      <div class="h-full w-[1px] bg-slate-600 my-2"></div>
                      <div class="h-full flex items-center cursor-pointer hover:text-red-500 p-2"
                           @click.stop="myFilters.stores = []">
                        <Icon icon="si:close-fill"/>
                      </div>
                    </div>
                  </div>
                  <div v-if="filters.includes('games')" class="flex flex-col gap-1 text-sm">
                    <p>Games</p>
                    <div @click="ordersStore.show_games_modal()"
                         class="cursor-pointer flex flex-row relative bg-slate-900 items-center justify-between rounded-lg">
                      <div class="flex-1 flex flex-row justify-between items-center p-2">
                        <div class="">
                          {{ myFilters.games.length }} Games
                        </div>
                        <span class="flex-none flex flex-row gap-2 h-full items-center">
                          <Icon icon="cuida:caret-down-outline"/>
                        </span>
                      </div>
                      <div class="h-full w-[1px] bg-slate-600 my-2"></div>
                      <div class="h-full flex items-center cursor-pointer hover:text-red-500 p-2"
                           @click.stop="myFilters.games = []">
                        <Icon icon="si:close-fill"/>
                      </div>
                    </div>
                  </div>
                  <div v-if="filters.includes('states')" class="flex flex-col gap-1 text-sm">
                    <p>States</p>
                    <div @click="ordersStore.show_states_modal()"
                         class="cursor-pointer flex flex-row relative bg-slate-900 items-center justify-between rounded-lg">
                      <div class="flex-1 flex flex-row justify-between items-center p-2">
                        <div class="">
                          {{ myFilters.states.length }} States
                        </div>
                        <span class="flex-none flex flex-row gap-2 h-full items-center">
                          <Icon icon="cuida:caret-down-outline"/>
                        </span>
                      </div>
                      <div class="h-full w-[1px] bg-slate-600 my-2"></div>
                      <div class="h-full flex items-center cursor-pointer hover:text-red-500 p-2"
                           @click.stop="myFilters.states = []">
                        <Icon icon="si:close-fill"/>
                      </div>
                    </div>
                  </div>
                  <div v-if="filters.includes('paymentMethods')" class="flex flex-col gap-1 text-sm">
                    <p>Payment Methods</p>
                    <div @click="ordersStore.show_paymentMethods_modal()"
                         class="cursor-pointer flex flex-row relative bg-slate-900 items-center justify-between rounded-lg">
                      <div class="flex-1 flex flex-row justify-between items-center p-2">
                        <div class="">
                          {{ myFilters.paymentMethods.length }} Methods
                        </div>
                        <span class="flex-none flex flex-row gap-2 h-full items-center">
                          <Icon icon="cuida:caret-down-outline"/>
                        </span>
                      </div>
                      <div class="h-full w-[1px] bg-slate-600 my-2"></div>
                      <div class="h-full flex items-center cursor-pointer hover:text-red-500 p-2"
                           @click.stop="myFilters.paymentMethods = []">
                        <Icon icon="si:close-fill"/>
                      </div>
                    </div>
                  </div>
                  <div v-if="filters.includes('shopStates')" class="flex flex-col gap-1 text-sm">
                    <p>Shop States</p>
                    <div @click="ordersStore.show_shop_states_modal()"
                         class="cursor-pointer flex flex-row relative bg-slate-900 items-center justify-between rounded-lg">
                      <div class="flex-1 flex flex-row justify-between items-center p-2">
                        <div class="">
                          {{ myFilters.shopStates.length }} Shop States
                        </div>
                        <span class="flex-none flex flex-row gap-2 h-full items-center">
                          <Icon icon="cuida:caret-down-outline"/>
                        </span>
                      </div>
                      <div class="h-full w-[1px] bg-slate-600 my-2"></div>
                      <div class="h-full flex items-center cursor-pointer hover:text-red-500 p-2"
                           @click.stop="myFilters.shopStates = []">
                        <Icon icon="si:close-fill"/>
                      </div>
                    </div>
                  </div>
                </div>
                <Dates
                    :key="counter"
                    :fields="dateFields"
                    :createdFrom="myFilters.createdFrom" :createdTo="myFilters.createdTo"
                    :updatedFrom="myFilters.updatedFrom" :updatedTo="myFilters.updatedTo"
                    @updateCreatedFrom="updateCreatedFrom" @updateCreatedTo="updateCreatedTo"
                    @updateUpdatedFrom="updateUpdatedFrom" @updateUpdatedTo="updateUpdatedTo"/>
                <button class="btn btn-outline-dark" @click="applyFilters">Apply</button>
              </div>
              <Stores v-if="ordersStore.isShowStoresModal" @update="updateStores" :stores="myFilters.stores"/>
              <PaymentMethods v-if="ordersStore.isShowPaymentMethodsModal" @update="updatePaymentMethods" :methods="myFilters.paymentMethods"/>
              <Games v-if="ordersStore.isShowGamesModal" @update="updateGames" :games="myFilters.games" :service="service"/>
              <States v-if="ordersStore.isShowStatesModal" @update="updateStates" :states="myFilters.states"/>
              <ShopStates v-if="ordersStore.isShowShopStatesModal" @update="updateShopStates" :states="myFilters.shopStates"/>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped lang="scss">

</style>